import { createOrderModel } from '../../components/DepthOfMarket/helpers'
import { OrderFields } from '../../helpers/hooks/useManageMyOrders'
import { OrderEntryModel } from '../depthOfMarket/types'
import { OrderType, TobParameters } from '../order/types'
import { Security } from '../securities/reducer'
import { StagedOrdersAction } from './actions'
import { workingOrderMiniReducer } from './helpers'

export interface StagedOrderInfo {
  price: number | undefined
  spread: number | undefined
  isSpreadOrder: boolean
  size: number
  allOrNone: boolean
  individualMin: number
  custId: number
  tob: TobParameters
  totalSize?: number
}

export interface StagedOrderType {
  ['buy']?: StagedOrderInfo
  ['sell']?: StagedOrderInfo
}

export interface State {
  stagedOrders: Record<Security['id'], StagedOrderType>
  focusedOrder?: { securityId: Security['id']; orderType: OrderType }
  tempFieldValues?: {
    [id: Security['id']]: {
      [key in OrderType]?: {
        [field in OrderFields]?: string
      }
    }
  }
  workingOrder?: OrderEntryModel
}

export const initialState: State = {
  stagedOrders: {}
}

const getNewTempFieldValues = (
  oldTempFieldValues: State['tempFieldValues'],
  securityId?: Security['id']
) => {
  if (oldTempFieldValues?.[securityId || -1]) {
    return { tempFieldValues: oldTempFieldValues }
  }
  return {}
}

export default (state = initialState, action: StagedOrdersAction): State => {
  switch (action.type) {
    case 'stagedOrders.addOrUpdateStagedOrders': {
      const { tempFieldValues, ...oldState } = state
      return {
        ...oldState,
        stagedOrders: action.payload.stagedOrders.reduce(
          (stagedOrders, stagedOrder) => {
            const {
              securityId,
              orderType,
              price,
              spread,
              isSpreadOrder,
              size,
              allOrNone,
              custId,
              tob,
              totalSize,
              individualMin
            } = stagedOrder
            if (((price || spread) && size) || totalSize) {
              return {
                ...stagedOrders,
                [securityId]: {
                  ...stagedOrders[securityId],
                  [orderType]: {
                    price,
                    spread,
                    isSpreadOrder,
                    size,
                    allOrNone,
                    custId,
                    tob,
                    totalSize: totalSize ?? size,
                    individualMin
                  }
                }
              }
            }
            return { ...stagedOrders }
          },
          state.stagedOrders
        )
      }
    }
    case 'stagedOrders.removeStagedOrder': {
      const { tempFieldValues, ...oldState } = state
      if (
        state.stagedOrders[action.payload.securityId] === undefined ||
        state.stagedOrders[action.payload.securityId][
          action.payload.orderType
        ] === undefined
      ) {
        // return prev state if securityId is not in stagedOrders state
        return oldState
      }

      const { [action.payload.orderType]: _noType, ...newStagedOrder } =
        state.stagedOrders[action.payload.securityId] // newStagedOrder may or may not be empty; {} | { 'buy': {..} }

      if (Object.entries(newStagedOrder).length === 0) {
        // remove entry altogether if obj is empty
        const { [action.payload.securityId]: _noId, ...orders } =
          state.stagedOrders
        return { ...oldState, stagedOrders: orders }
      }

      return {
        ...oldState,
        stagedOrders: {
          ...oldState.stagedOrders,
          [action.payload.securityId]: newStagedOrder
        }
      }
    }
    case 'stagedOrders.removeStagedOrders': {
      const { stagedOrders, tempFieldValues, ...oldState } = state

      const st = { ...stagedOrders }
      // TODO: this originated as mutation code and needs to be cleaned up
      for (const SO of action.payload) {
        if (st.hasOwnProperty(SO.securityId)) {
          st[SO.securityId][SO.orderType] = undefined
          if (
            st[SO.securityId].buy === undefined &&
            st[SO.securityId].sell === undefined
          ) {
            delete st[SO.securityId]
          }
        }
      }
      return {
        ...oldState,
        stagedOrders: st
      }
    }

    case 'stagedOrders.removeAllStagedOrders': {
      const { stagedOrders, tempFieldValues, ...oldState } = state
      return { ...oldState, stagedOrders: {} }
    }

    case 'stagedOrders.setFocusOnOrder': {
      const { tempFieldValues: oldTempfieldValues, ...oldState } = state
      const newTempfieldValues = getNewTempFieldValues(
        oldTempfieldValues,
        action.payload?.securityId
      )
      const stagedOrder = action.payload
        ? oldState.stagedOrders[action.payload.securityId]?.[
            action.payload.orderType
          ]
        : undefined
      const tempSize = action.payload
        ? state.tempFieldValues?.[action.payload.securityId || -1]?.[
            action.payload.orderType
          ]?.size || ''
        : ''
      const prevSize = stagedOrder ? `${stagedOrder.size || ''}` : tempSize
      // right now, we don't care about UST formatting in My Orders, so nothing sends the isUst flag
      const newWorkingOrder = action.payload
        ? {
            ...createOrderModel(
              stagedOrder,
              action.payload.orderType,
              !!action.payload.isUst
            ),
            totalSize: prevSize
          }
        : undefined
      // only use temp values on initial focus
      const focusHasChanged =
        oldState.focusedOrder?.securityId !== action.payload?.securityId ||
        oldState.focusedOrder?.orderType !== action.payload?.orderType
      const workingOrder =
        focusHasChanged || !oldState.workingOrder
          ? newWorkingOrder
          : oldState.workingOrder
      return {
        ...oldState,
        focusedOrder: action.payload,
        ...newTempfieldValues,
        workingOrder
      }
    }
    case 'stagedOrders.setTempOrderFieldValue':
      // we deliberately only keep temp values for one security at a time
      const prevForThisSecurity =
        state.tempFieldValues?.[action.securityId] ?? {}
      return {
        ...state,
        tempFieldValues: {
          [action.securityId]: {
            ...prevForThisSecurity,
            [action.orderType]: { [action.field]: action.payload }
          }
        }
      }
    case 'stagedOrders.updateWorkingOrderField': {
      const model = state.workingOrder
      if (!model) return state
      return { ...state, workingOrder: workingOrderMiniReducer(model, action) }
    }
    case 'stagedOrders.clearCheckedStagedOrdersAction': {
      const {
        tempFieldValues: oldTempfieldValues,
        workingOrder: wo,
        ...oldState
      } = state
      return oldState
    }

    default:
      return state
  }
}
